import { faCircleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Anchor } from "~/components/Anchor";
import { Tooltip } from "~/components/Tooltip";
import { Networks } from "~/schema";
import { Token } from "../../types";
import { cn } from "~/lib/utils";

// // // //

const USDC = "USDC";
const USDYC_ADDRESS = "0xe86845788d6e3e5c2393ade1a051ae617d974c09";

interface TokenMessage {
    title: string | null;
    body: string;
    href: string | null;
    centered?: boolean;
}

function getTokenMessage(props: {
    ticker: null | string;
    token: Token;
}): TokenMessage | null {
    const { ticker, token } = props;
    const networkSlug = token.network?.slug ?? null;

    if (networkSlug === Networks.tron && ticker === USDC) {
        return {
            title: null,
            body: "Circle is discontinuing support for USDC on the TRON Blockchain.",
            href: "https://www.circle.com/blog/circle-is-discontinuing-support-for-usdc-on-the-tron-blockchain",
            centered: true,
        };
    }

    if (token.address === USDYC_ADDRESS) {
        return {
            title: "Note from Ondo:",
            body: "This is USDY in \"Certificate\" form, where a customer has purchased tokens from us, but it's not fully unlocked - they can only transfer it to other KYC'd non-US investors. We use this token as an accounting mechanism to bring this off-chain data onchain.",
            href: null,
        };
    }

    return null;
}

// // // //

export function TokenMessages(props: { ticker: null | string; token: Token }) {
    // Get the message if available
    const message = getTokenMessage(props);

    // Return null if no message is available
    if (!message) return null;

    // Build className based on message.type
    const messageClassName =
        "transition-colors text-amber-500 hover:text-amber-600";

    // Build the icon for the tooltip
    const icon = (
        <FontAwesomeIcon
            icon={faCircleExclamation}
            className={cn("h-3.5 ml-1.5", {
                [messageClassName]: message.href === null,
            })}
        />
    );

    return (
        <Tooltip
            placement="top"
            tooltipWrapperClassName="rounded-lg shadow-sm select-none border"
            tooltipContent={
                <div className="max-w-160 px-3">
                    {message.title !== null && (
                        <p
                            className={cn("pt-1.5 text-sm text-foreground", {
                                "text-center": message.centered,
                            })}
                        >
                            {message.title}
                        </p>
                    )}

                    <p
                        className={cn("text-xs text-foreground", {
                            "py-1.5": message.title === null,
                            "pb-1.5": message.title !== null,
                            "text-center": message.centered,
                        })}
                    >
                        {message.body}

                        {message.href !== null && (
                            <>
                                <br />
                                Click to read more.
                            </>
                        )}
                    </p>
                </div>
            }
        >
            {({ setTriggerRef }) => (
                <span ref={setTriggerRef}>
                    {message.href !== null && (
                        <Anchor
                            openInNewTab
                            analyticsLabel="TokenWarning CTA"
                            className={messageClassName}
                            href={message.href}
                        >
                            {icon}
                        </Anchor>
                    )}

                    {message.href === null && <>{icon}</>}
                </span>
            )}
        </Tooltip>
    );
}
